'use client'

import { useState, useEffect, useMemo, Suspense } from 'react'
import { useSearchParams, useRouter } from 'next/navigation'
import Link from 'next/link'
import { TopBar, BottomNav } from '@/components/layout/navigation'
import CakeCard from '@/components/cake/cake-card'
import { Search, X, Loader2, TrendingUp, Clock, Delete } from 'lucide-react'

interface CakeItem {
  id: number
  name: string
  description: string
  minPrice: number
  maxPrice: number
  image: string
  category: string
  style: string
  tags: string[]
  rating: number
  monthlySales: number
  isHot: boolean
  isNew: boolean
  isSeasonal: boolean
  sizes: string[]
}

const HOT_KEYWORDS = ['草莓', '巧克力', '生日蛋糕', '抹茶', '榴莲', '水果', '定制', '儿童']

function SearchContent() {
  const searchParams = useSearchParams()
  const router = useRouter()
  const initialKeyword = searchParams.get('q') || ''

  const [allCakes, setAllCakes] = useState<CakeItem[]>([])
  const [loading, setLoading] = useState(true)
  const [keyword, setKeyword] = useState(initialKeyword)
  const [searchHistory, setSearchHistory] = useState<string[]>([])

  useEffect(() => {
    fetch('/api/cakes?pageSize=100')
      .then(res => res.json())
      .then(data => setAllCakes(data.items || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    const saved = localStorage.getItem('cake-shop-search-history')
    if (saved) setSearchHistory(JSON.parse(saved))
  }, [])

  const saveHistory = (kw: string) => {
    const trimmed = kw.trim()
    if (!trimmed) return
    const updated = [trimmed, ...searchHistory.filter(h => h !== trimmed)].slice(0, 10)
    setSearchHistory(updated)
    localStorage.setItem('cake-shop-search-history', JSON.stringify(updated))
  }

  const clearHistory = () => {
    setSearchHistory([])
    localStorage.removeItem('cake-shop-search-history')
  }

  const results = useMemo(() => {
    if (!keyword.trim()) return []
    const kw = keyword.toLowerCase()
    return allCakes.filter(c =>
      c.name.toLowerCase().includes(kw) ||
      c.description.toLowerCase().includes(kw) ||
      c.tags.some(t => t.includes(kw)) ||
      c.category.includes(kw) ||
      c.taste.includes(kw)
    )
  }, [allCakes, keyword])

  const handleSearch = (kw: string) => {
    setKeyword(kw)
    saveHistory(kw)
  }

  const handleSubmit = () => {
    if (keyword.trim()) {
      saveHistory(keyword)
    }
  }

  const isSearching = keyword.trim().length > 0

  return (
    <div className="min-h-screen pb-16 bg-gray-50">
      <TopBar />

      {/* 搜索栏 */}
      <div className="sticky top-12 z-30 bg-white px-4 py-2 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <div className="flex-1 flex items-center bg-gray-100 rounded-full px-3 h-9">
            <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
              placeholder="搜索蛋糕、口味、分类..."
              className="flex-1 bg-transparent text-sm ml-2 outline-none placeholder:text-gray-400"
              autoFocus
            />
            {keyword && (
              <button onClick={() => setKeyword('')}>
                <X className="w-4 h-4 text-gray-400" />
              </button>
            )}
          </div>
          <button onClick={() => router.back()} className="text-sm text-gray-500 whitespace-nowrap">
            取消
          </button>
        </div>
      </div>

      {/* 搜索结果 */}
      {isSearching && (
        <div className="px-4 pt-3">
          {loading ? (
            <div className="flex items-center justify-center pt-16">
              <Loader2 className="w-6 h-6 text-pink-500 animate-spin" />
            </div>
          ) : results.length > 0 ? (
            <>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs text-gray-400">找到 {results.length} 个结果</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {results.map(cake => <CakeCard key={cake.id} cake={cake} />)}
              </div>
            </>
          ) : (
            <div className="text-center py-20">
              <Search className="w-12 h-12 text-gray-200 mx-auto mb-3" />
              <p className="text-sm text-gray-400">没有找到「{keyword}」相关的蛋糕</p>
              <p className="text-xs text-gray-300 mt-1">换个关键词试试吧</p>
            </div>
          )}
        </div>
      )}

      {/* 默认状态：热词 + 搜索历史 */}
      {!isSearching && (
        <div className="px-4 pt-4">
          {/* 搜索历史 */}
          {searchHistory.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-sm font-bold text-gray-700">搜索历史</span>
                </div>
                <button onClick={clearHistory} className="text-xs text-gray-400 flex items-center gap-0.5">
                  <Delete className="w-3 h-3" /> 清除
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {searchHistory.map((kw, idx) => (
                  <button key={idx} onClick={() => handleSearch(kw)}
                    className="px-3 py-1.5 bg-gray-100 text-gray-600 text-xs rounded-full hover:bg-gray-200 transition-colors active:scale-95">
                    {kw}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 热门搜索 */}
          <div>
            <div className="flex items-center gap-1.5 mb-3">
              <TrendingUp className="w-3.5 h-3.5 text-orange-400" />
              <span className="text-sm font-bold text-gray-700">热门搜索</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {HOT_KEYWORDS.map((kw, idx) => (
                <button key={kw} onClick={() => handleSearch(kw)}
                  className="px-3 py-1.5 bg-orange-50 text-orange-600 text-xs rounded-full hover:bg-orange-100 transition-colors active:scale-95">
                  {idx < 3 && <span className="text-orange-400 mr-1 font-bold">{idx + 1}</span>}
                  {kw}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  )
}

export default function SearchPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen pb-16">
        <TopBar />
        <div className="pt-20 text-center text-sm text-gray-400">加载中...</div>
        <BottomNav />
      </div>
    }>
      <SearchContent />
    </Suspense>
  )
}
